import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, Search, Send, Users, CheckSquare, Square, Check, X,
  FileText, Clock, AlertCircle, ChevronDown, Loader2, History,
  Paperclip, Upload, Trash2, FolderOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { comunicacionesAPI, cuentasEmailAPI, expedientesAPI, adjuntosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const Comunicaciones = () => {
  const [clientes, setClientes] = useState([]);
  const [selectedClientes, setSelectedClientes] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);
  
  // Cuentas de email
  const [cuentasEmail, setCuentasEmail] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState('');
  
  // Estado del formulario
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [selectedPlantilla, setSelectedPlantilla] = useState('');
  
  // Diálogos
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  
  // Archivos adjuntos
  const [adjuntos, setAdjuntos] = useState([]);
  const fileInputRef = useRef(null);
  
  // Crear actuación en expediente
  const [crearActuacion, setCrearActuacion] = useState(false);
  const [expedientes, setExpedientes] = useState([]);
  const [selectedExpediente, setSelectedExpediente] = useState('');
  const [loadingExpedientes, setLoadingExpedientes] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientesRes, plantillasRes, historialRes, statsRes, cuentasRes] = await Promise.all([
        comunicacionesAPI.getClientes(),
        comunicacionesAPI.getPlantillas(),
        comunicacionesAPI.getHistorial(),
        comunicacionesAPI.getEstadisticas(),
        cuentasEmailAPI.getActivas()
      ]);
      setClientes(clientesRes.data.data || clientesRes.data || []);
      setPlantillas(plantillasRes.data.data || plantillasRes.data || []);
      setHistorial(historialRes.data.historial || []);
      setEstadisticas(statsRes.data);
      setCuentasEmail(cuentasRes.data.data || cuentasRes.data || []);
      
      // Seleccionar cuenta principal por defecto
      const principal = cuentasRes.data.find(c => c.es_principal);
      if (principal) {
        setSelectedCuenta(principal.id.toString());
      } else if (cuentasRes.data.length > 0) {
        setSelectedCuenta(cuentasRes.data[0].id.toString());
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar expedientes cuando se activa la opción de crear actuación
  const loadExpedientes = async () => {
    if (expedientes.length > 0) return; // Ya cargados
    setLoadingExpedientes(true);
    try {
      const response = await expedientesAPI.getAll();
      setExpedientes(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error cargando expedientes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los expedientes",
        variant: "destructive"
      });
    } finally {
      setLoadingExpedientes(false);
    }
  };

  // Manejar archivos adjuntos
  const handleAddFiles = (files) => {
    const newFiles = Array.from(files);
    setAdjuntos(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setAdjuntos(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredClientes = clientes.filter(c => 
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id_interno.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectCliente = (clienteId) => {
    setSelectedClientes(prev => 
      prev.includes(clienteId) 
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId]
    );
  };

  const selectAll = () => {
    if (selectedClientes.length === filteredClientes.length) {
      setSelectedClientes([]);
    } else {
      setSelectedClientes(filteredClientes.map(c => c.id));
    }
  };

  const handlePlantillaChange = (plantillaId) => {
    setSelectedPlantilla(plantillaId);
    if (plantillaId && plantillaId !== 'none') {
      const plantilla = plantillas.find(p => p.id === parseInt(plantillaId));
      if (plantilla) {
        setAsunto(plantilla.asunto);
        setMensaje(plantilla.mensaje);
      }
    }
  };

  const getTipoEnvio = () => {
    if (selectedClientes.length === 0) return null;
    if (selectedClientes.length === 1) return 'individual';
    if (selectedClientes.length === filteredClientes.length && filteredClientes.length > 1) return 'masivo';
    return 'grupo';
  };

  const handlePrepareEnvio = () => {
    if (selectedClientes.length === 0) {
      toast({
        title: "Selecciona destinatarios",
        description: "Debes seleccionar al menos un cliente",
        variant: "destructive"
      });
      return;
    }
    if (!selectedCuenta) {
      toast({
        title: "Selecciona cuenta de envío",
        description: "Debes seleccionar desde qué cuenta enviar",
        variant: "destructive"
      });
      return;
    }
    if (!asunto.trim()) {
      toast({
        title: "Asunto requerido",
        description: "El asunto del mensaje es obligatorio",
        variant: "destructive"
      });
      return;
    }
    if (!mensaje.trim()) {
      toast({
        title: "Mensaje requerido",
        description: "El contenido del mensaje es obligatorio",
        variant: "destructive"
      });
      return;
    }
    setIsConfirmDialogOpen(true);
  };

  const handleEnviar = async () => {
    setSending(true);
    try {
      // Preparar FormData si hay adjuntos
      let response;
      if (adjuntos.length > 0) {
        const formData = new FormData();
        formData.append('destinatarios_ids', JSON.stringify(selectedClientes));
        formData.append('asunto', asunto);
        formData.append('mensaje', mensaje);
        formData.append('tipo_envio', getTipoEnvio());
        formData.append('cuenta_id', selectedCuenta);
        
        // Añadir archivos
        adjuntos.forEach((file) => {
          formData.append('adjuntos', file);
        });
        
        response = await comunicacionesAPI.enviarConAdjuntos(formData);
      } else {
        response = await comunicacionesAPI.enviar({
          destinatarios_ids: selectedClientes,
          asunto: asunto,
          mensaje: mensaje,
          tipo_envio: getTipoEnvio(),
          cuenta_id: parseInt(selectedCuenta)
        });
      }
      
      // Crear actuación si está seleccionado
      if (crearActuacion && selectedExpediente) {
        try {
          const destinatariosInfo = getSelectedClientesInfo();
          const destinatariosNombres = destinatariosInfo.map(c => [c.nombre, c.primer_apellido, c.segundo_apellido].filter(Boolean).join(' ')).join(', ');
          const adjuntosNombres = adjuntos.map(f => f.name).join(', ');
          
          const actuacionData = {
            titulo: `Comunicación enviada: ${asunto}`,
            fecha: new Date().toISOString(),
            tipo: 'comunicacion',
            descripcion: `**Comunicación enviada a cliente(s)**\n\n` +
              `**Destinatarios:** ${destinatariosNombres}\n\n` +
              `**Asunto:** ${asunto}\n\n` +
              `**Mensaje:**\n${mensaje}` +
              (adjuntosNombres ? `\n\n**Archivos adjuntos:** ${adjuntosNombres}` : ''),
            observaciones: `Comunicación enviada el ${new Date().toLocaleString('es-ES')}`
          };
          
          const actuacionResponse = await expedientesAPI.createActuacion(selectedExpediente, actuacionData);
          
          // Subir adjuntos a la actuación si existen
          if (adjuntos.length > 0 && actuacionResponse.data?.id) {
            for (const file of adjuntos) {
              const adjuntoFormData = new FormData();
              adjuntoFormData.append('file', file);
              adjuntoFormData.append('tipo_adjunto', 'documento');
              try {
                await adjuntosAPI.uploadActuacion(actuacionResponse.data.id, adjuntoFormData);
              } catch (err) {
                console.error('Error subiendo adjunto a actuación:', err);
              }
            }
          }
          
          toast({
            title: "Actuación creada",
            description: `Se ha registrado la comunicación en el expediente`
          });
        } catch (actError) {
          console.error('Error creando actuación:', actError);
          toast({
            title: "Aviso",
            description: "Email enviado pero no se pudo crear la actuación",
            variant: "destructive"
          });
        }
      }
      
      setSendResult(response.data);
      setIsConfirmDialogOpen(false);
      setIsResultDialogOpen(true);
      
      // Limpiar formulario
      setSelectedClientes([]);
      setAsunto('');
      setMensaje('');
      setSelectedPlantilla('');
      setAdjuntos([]);
      setCrearActuacion(false);
      setSelectedExpediente('');
      
      // Recargar historial y estadísticas
      const [historialRes, statsRes] = await Promise.all([
        comunicacionesAPI.getHistorial(),
        comunicacionesAPI.getEstadisticas()
      ]);
      setHistorial(historialRes.data.historial || []);
      setEstadisticas(statsRes.data);
      
    } catch (error) {
      console.error('Error enviando comunicación:', error);
      toast({
        title: "Error",
        description: "No se pudo enviar la comunicación",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const getSelectedClientesInfo = () => {
    return clientes.filter(c => selectedClientes.includes(c.id));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comunicaciones</h1>
          <p className="text-gray-600 mt-1">Envío de comunicaciones a clientes</p>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Clientes con email</p>
                <p className="text-xl font-bold">{estadisticas.clientes_con_email || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Contactos con email</p>
                <p className="text-xl font-bold">{estadisticas.contactos_con_email || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Send className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Comunicaciones enviadas</p>
                <p className="text-xl font-bold">{estadisticas.total_comunicaciones || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Emails enviados</p>
                <p className="text-xl font-bold">{estadisticas.total_emails_enviados || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="enviar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="enviar" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Enviar Comunicación
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="enviar">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de clientes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Seleccionar Destinatarios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Barra de búsqueda y selección */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Buscar cliente..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button variant="outline" onClick={selectAll}>
                    {selectedClientes.length === filteredClientes.length && filteredClientes.length > 0
                      ? 'Deseleccionar todos'
                      : 'Seleccionar todos'}
                  </Button>
                </div>

                {/* Info de selección */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {filteredClientes.length} clientes disponibles
                  </span>
                  <Badge variant="secondary">
                    {selectedClientes.length} seleccionados
                  </Badge>
                </div>

                {/* Lista de clientes */}
                <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                  {filteredClientes.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No hay clientes con email
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredClientes.map((cliente) => (
                        <div
                          key={cliente.id}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedClientes.includes(cliente.id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => toggleSelectCliente(cliente.id)}
                        >
                          <Checkbox
                            checked={selectedClientes.includes(cliente.id)}
                            onCheckedChange={() => toggleSelectCliente(cliente.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{[cliente.nombre, cliente.primer_apellido, cliente.segundo_apellido].filter(Boolean).join(" ") || cliente.razon_social || "Sin nombre"}</span>
                              <Badge variant="outline" className="text-xs">{cliente.id_interno}</Badge>
                            </div>
                            <p className="text-sm text-gray-500 truncate">{cliente.email}</p>
                          </div>
                          {selectedClientes.includes(cliente.id) && (
                            <Check className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Formulario de mensaje */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Redactar Mensaje
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Selector de cuenta de envío */}
                <div>
                  <Label>Enviar desde *</Label>
                  <Select value={selectedCuenta} onValueChange={setSelectedCuenta}>
                    <SelectTrigger className={!selectedCuenta ? 'border-red-300' : ''}>
                      <SelectValue placeholder="Seleccionar cuenta de email" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuentasEmail.length === 0 ? (
                        <SelectItem value="none" disabled>No hay cuentas configuradas</SelectItem>
                      ) : (
                        cuentasEmail.map(cuenta => (
                          <SelectItem key={cuenta.id} value={String(cuenta.id)}>
                            <span className="flex items-center gap-2">
                              {cuenta.es_principal && <span className="text-yellow-500">★</span>}
                              {cuenta.nombre} ({cuenta.email})
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {cuentasEmail.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      Configura cuentas de email en Configuración → Cuentas de Email
                    </p>
                  )}
                </div>

                {/* Plantillas */}
                <div>
                  <Label>Usar plantilla</Label>
                  <Select value={selectedPlantilla} onValueChange={handlePlantillaChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin plantilla</SelectItem>
                      {plantillas.map(p => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Asunto */}
                <div>
                  <Label htmlFor="asunto">Asunto *</Label>
                  <Input
                    id="asunto"
                    value={asunto}
                    onChange={(e) => setAsunto(e.target.value)}
                    placeholder="Asunto del mensaje"
                  />
                </div>

                {/* Mensaje */}
                <div>
                  <Label htmlFor="mensaje">Mensaje *</Label>
                  <Textarea
                    id="mensaje"
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    placeholder="Escribe el contenido del mensaje..."
                    rows={8}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Usa [NOMBRE] para personalizar con el nombre del destinatario
                  </p>
                </div>

                {/* Archivos adjuntos */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" /> Archivos adjuntos
                  </Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    multiple
                    onChange={(e) => handleAddFiles(e.target.files)}
                  />
                  <div 
                    className="mt-2 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-600">Clic para adjuntar archivos</p>
                  </div>
                  
                  {adjuntos.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {adjuntos.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1.5 px-2 bg-gray-100 rounded text-sm">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{file.name}</span>
                            <span className="text-gray-400 text-xs flex-shrink-0">
                              ({formatFileSize(file.size)})
                            </span>
                          </div>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0"
                            onClick={() => handleRemoveFile(idx)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Crear actuación en expediente */}
                <div className="border rounded-lg p-3 bg-blue-50/50">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="crearActuacion"
                      checked={crearActuacion}
                      onCheckedChange={(checked) => {
                        setCrearActuacion(checked);
                        if (checked) {
                          loadExpedientes();
                        }
                      }}
                    />
                    <Label htmlFor="crearActuacion" className="cursor-pointer flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-blue-600" />
                      Registrar como actuación en expediente
                    </Label>
                  </div>
                  
                  {crearActuacion && (
                    <div className="mt-3">
                      <Label className="text-xs">Seleccionar expediente *</Label>
                      <Select 
                        value={selectedExpediente} 
                        onValueChange={setSelectedExpediente}
                        disabled={loadingExpedientes}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder={loadingExpedientes ? "Cargando..." : "Seleccionar expediente"} />
                        </SelectTrigger>
                        <SelectContent>
                          {expedientes.map(exp => (
                            <SelectItem key={exp.id} value={String(exp.id)}>
                              {exp.numero_expediente} - {exp.titulo}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-blue-600 mt-1">
                        Se creará una actuación con el contenido del mensaje y los archivos adjuntos
                      </p>
                    </div>
                  )}
                </div>

                {/* Tipo de envío */}
                {selectedClientes.length > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Tipo de envío:</span>
                      <Badge className={
                        getTipoEnvio() === 'individual' ? 'bg-blue-100 text-blue-700' :
                        getTipoEnvio() === 'grupo' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }>
                        {getTipoEnvio() === 'individual' ? 'Individual' :
                         getTipoEnvio() === 'grupo' ? 'Grupo' : 'Masivo'}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Botón enviar */}
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handlePrepareEnvio}
                  disabled={selectedClientes.length === 0 || !asunto || !mensaje}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Preparar Envío ({selectedClientes.length} destinatarios)
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="historial">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historial de Comunicaciones</CardTitle>
            </CardHeader>
            <CardContent>
              {historial.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay comunicaciones enviadas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asunto</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destinatarios</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {historial.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(item.fecha_envio).toLocaleString('es-ES')}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {item.asunto}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{item.tipo_envio}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.total_destinatarios}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-700">
                                {item.enviados_exitosos} enviados
                              </Badge>
                              {item.enviados_fallidos > 0 && (
                                <Badge className="bg-red-100 text-red-700">
                                  {item.enviados_fallidos} fallidos
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Diálogo de confirmación */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmar envío</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                Vas a enviar una comunicación a <strong>{selectedClientes.length}</strong> destinatario(s)
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Asunto:</p>
              <p className="text-sm text-gray-600 p-2 bg-gray-50 rounded">{asunto}</p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Destinatarios:</p>
              <div className="max-h-32 overflow-y-auto border rounded p-2">
                {getSelectedClientesInfo().map(c => (
                  <div key={c.id} className="text-sm text-gray-600 py-1">
                    {[c.nombre, c.primer_apellido, c.segundo_apellido].filter(Boolean).join(" ") || c.razon_social} ({c.email})
                  </div>
                ))}
              </div>
            </div>

            {/* Mostrar adjuntos si hay */}
            {adjuntos.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Archivos adjuntos ({adjuntos.length}):
                </p>
                <div className="border rounded p-2 bg-gray-50">
                  {adjuntos.map((file, idx) => (
                    <div key={idx} className="text-sm text-gray-600 py-0.5 flex items-center gap-2">
                      <FileText className="w-3 h-3" />
                      {file.name} ({formatFileSize(file.size)})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mostrar si se creará actuación */}
            {crearActuacion && selectedExpediente && (
              <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                <FolderOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-700">Se creará actuación en expediente</p>
                  <p className="text-xs text-blue-600">
                    {expedientes.find(e => String(e.id) === selectedExpediente)?.numero_expediente} - {expedientes.find(e => String(e.id) === selectedExpediente)?.titulo}
                  </p>
                </div>
              </div>
            )}

            <div className="p-3 bg-yellow-50 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <p className="text-sm text-yellow-700">
                Esta acción no se puede deshacer. Los emails se enviarán inmediatamente.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEnviar} disabled={sending}>
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Confirmar Envío
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de resultado */}
      <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sendResult?.enviados_fallidos === 0 ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              )}
              Resultado del envío
            </DialogTitle>
          </DialogHeader>
          
          {sendResult && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${
                sendResult.enviados_fallidos === 0 ? 'bg-green-50' : 'bg-yellow-50'
              }`}>
                <p className={`text-sm ${
                  sendResult.enviados_fallidos === 0 ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {sendResult.message}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{sendResult.enviados_exitosos}</p>
                  <p className="text-xs text-gray-500">Enviados</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{sendResult.enviados_fallidos}</p>
                  <p className="text-xs text-gray-500">Fallidos</p>
                </div>
              </div>

              {sendResult.detalles && sendResult.detalles.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded p-2">
                  {sendResult.detalles.map((d, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-gray-600">{d.destinatario_email}</span>
                      <Badge className={d.estado === 'enviado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {d.estado}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setIsResultDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Comunicaciones;
